import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiError, Badge, Button, Card, CardContent, CardHeader, CardTitle, FilterChip, SkeletonCard } from "@/components/ui";
import { getErrorMessage, isApiError } from "@/api/client";
import { centsToUsd } from "@/lib/money";
import {
  MEMBERSHIP_STATUS_BADGE_VARIANT,
  MEMBERSHIP_STATUS_LABELS,
  type MembershipStatus,
} from "@/api/types/membership";
import { formatBalanceSummary, formatTimeRange, formatWeekRange, usagePercent } from "@/features/memberships/hourMath";
import {
  useMembershipPortalAddonOptions,
  useMembershipPortalMe,
  useMembershipPortalPurchaseAddonHours,
} from "./useMembershipPortal";

const BUSINESS_NAME = "The Wilder";
const INVALID_LINK_MESSAGE = "This link is invalid or has expired — please ask The Wilder for a new one.";

/**
 * PUBLIC membership portal (Phase 2b) — reached via a magic-link URL
 * (`/membership-portal?token=<jwt>`), never behind the staff auth guard
 * (see App.tsx: this route sits outside <RequireAuth>). Read-only by
 * design this phase: balance + upcoming sessions only — see
 * app/api/portal_membership.py's module docstring. Mirrors
 * features/portal/PortalPage.tsx's structure (dedicated axios client,
 * `enabled: !!token`, identical invalid-link posture) but is a
 * completely separate token/backend surface, not an extension of it.
 */
export function MembershipPortalPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const { data, isLoading, error, refetch } = useMembershipPortalMe(token);
  const isInvalidToken = isApiError(error) && error.response?.status === 401;

  return (
    <div className="min-h-screen bg-bg-body px-4 py-8 sm:py-12">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
        <header className="text-center">
          <p className="font-display text-sm font-semibold uppercase tracking-wide text-copper">{BUSINESS_NAME}</p>
          <h1 className="mt-1 font-display text-2xl font-bold text-text-primary sm:text-3xl">Your membership</h1>
        </header>

        {!token || isInvalidToken ? (
          <MembershipPortalInvalidLink />
        ) : isLoading ? (
          <div className="flex flex-col gap-6">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : error || !data ? (
          <ApiError error={error} onRetry={() => void refetch()} title="Unable to load your membership" />
        ) : (
          <>
            <MembershipStatusCard status={data.status as MembershipStatus} tierName={data.tier.name} storageAssigned={data.storage_assigned} />
            <MembershipBalanceCard balance={data.balance} />
            {data.status === "active" && <MembershipAddonHoursCard token={token as string} />}
            <MembershipUpcomingSessionsCard sessions={data.upcoming_sessions} />
          </>
        )}
      </div>
    </div>
  );
}

function MembershipPortalInvalidLink() {
  return (
    <Card className="text-center">
      <CardContent className="flex flex-col items-center gap-3 py-8">
        <span className="text-4xl" role="img" aria-label="Locked link">
          🔒
        </span>
        <p className="text-lg font-semibold text-text-primary">Link invalid or expired</p>
        <p className="max-w-sm text-sm text-text-secondary">{INVALID_LINK_MESSAGE}</p>
      </CardContent>
    </Card>
  );
}

function MembershipStatusCard({
  status,
  tierName,
  storageAssigned,
}: {
  status: MembershipStatus;
  tierName: string;
  storageAssigned: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>{tierName}</CardTitle>
          {storageAssigned && <p className="mt-1 text-sm text-text-secondary">Includes storage</p>}
        </div>
        <Badge variant={MEMBERSHIP_STATUS_BADGE_VARIANT[status]}>{MEMBERSHIP_STATUS_LABELS[status]}</Badge>
      </CardHeader>
    </Card>
  );
}

function MembershipBalanceCard({
  balance,
}: {
  balance: { week_start: string; week_end: string; quota: number | null; used: number; remaining: number | null };
}) {
  const pct = usagePercent(balance);
  return (
    <Card>
      <CardHeader>
        <CardTitle>This week's studio hours</CardTitle>
        <p className="mt-1 text-sm text-text-secondary">{formatWeekRange(balance.week_start, balance.week_end)}</p>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-lg font-semibold text-text-primary" data-testid="membership-portal-balance">
          {formatBalanceSummary(balance)}
        </p>
        {pct !== null && (
          <div className="h-2 w-full overflow-hidden rounded-full bg-bg-hover" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full bg-brass" style={{ width: `${pct}%` }} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Self-service add-on studio hours (migration 009) — owner's explicit
 * requirement: "Don't show those prices/options until they have already
 * signed up as a member so they can't compare additional hours." This
 * component is ONLY ever rendered from MembershipPortalPage above, which
 * itself only renders once a valid portal token has resolved a real
 * membership (see that component's `!token || isInvalidToken` gate) — so
 * "already signed up as a member" is satisfied by construction. It also
 * self-gates on `available` from the options endpoint: a tier with no
 * add-on rate configured renders nothing here, same as the backend
 * treating that as a normal (not error) outcome.
 */
function MembershipAddonHoursCard({ token }: { token: string }) {
  const { data: options, isLoading } = useMembershipPortalAddonOptions(token);
  const purchase = useMembershipPortalPurchaseAddonHours(token);

  const [hours, setHours] = useState<2 | 4 | 6>(2);
  const [weekStart, setWeekStart] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<{ hours: number; billingMode: string } | null>(null);

  if (isLoading || !options?.available || options.rate_cents_per_hour == null) {
    return null;
  }

  const rate = options.rate_cents_per_hour;
  const blockSizes = (options.block_sizes.length > 0 ? options.block_sizes : [2, 4, 6]) as Array<2 | 4 | 6>;
  const priceCents = rate * hours;

  const handlePurchase = () => {
    setPurchaseError(null);
    setOutcome(null);
    purchase.mutate(
      { week_start: weekStart, hours },
      {
        onSuccess: (result) => {
          if (result.billing_mode === "stripe-checkout-created" && result.checkout_url) {
            window.location.href = result.checkout_url;
            return;
          }
          setOutcome({ hours: result.hours, billingMode: result.billing_mode });
        },
        onError: (err) => setPurchaseError(getErrorMessage(err)),
      },
    );
  };

  return (
    <Card data-testid="membership-addon-hours-card">
      <CardHeader>
        <CardTitle>Add more studio hours this week</CardTitle>
        <p className="mt-1 text-sm text-text-secondary">{centsToUsd(rate)}/hr, billed for the week you choose.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label htmlFor="addon-week-start" className="mb-1 block text-sm font-medium text-text-primary">
            Week
          </label>
          <input
            id="addon-week-start"
            type="date"
            value={weekStart}
            onChange={(e) => setWeekStart(e.target.value)}
            className="flex h-10 w-full max-w-[12rem] rounded-md border border-border bg-bg-card px-3 py-2 text-sm shadow-2xs focus:outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/15"
          />
          <p className="mt-1 text-xs text-text-muted">Any day in the week works — hours apply Mon–Sun.</p>
        </div>

        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Additional hours">
          {blockSizes.map((size) => (
            <FilterChip
              key={size}
              label={`+${size}h`}
              active={hours === size}
              onClick={() => setHours(size)}
              data-testid={`addon-hours-block-${size}`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between rounded-md bg-bg-muted px-3 py-2">
          <span className="text-sm text-text-secondary">
            {hours}h &times; {centsToUsd(rate)}
          </span>
          <span className="text-lg font-semibold text-text-primary" data-testid="addon-hours-price">
            {centsToUsd(priceCents)}
          </span>
        </div>

        <Button onClick={handlePurchase} disabled={purchase.isPending} data-testid="addon-hours-purchase-button">
          {purchase.isPending ? "Adding…" : `Add ${hours}h for ${centsToUsd(priceCents)}`}
        </Button>

        {purchaseError && (
          <p className="text-sm text-danger" role="alert">
            {purchaseError}
          </p>
        )}

        {outcome && (
          <div className="rounded-md border border-border bg-bg-card p-3" data-testid="addon-hours-outcome">
            <p className="text-sm font-medium text-text-primary">Added {outcome.hours}h for that week.</p>
            {outcome.billingMode.startsWith("manual-billing") ? (
              <p className="mt-1 text-xs text-text-muted">We'll follow up separately about payment for these hours.</p>
            ) : (
              <p className="mt-1 text-xs text-text-muted">Your balance above now reflects the added hours.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MembershipUpcomingSessionsCard({
  sessions,
}: {
  sessions: Array<{ id: string; session_date: string; start_time: string; end_time: string; hours: number }>;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Upcoming studio sessions</CardTitle>
      </CardHeader>
      <CardContent>
        {sessions.length === 0 ? (
          <p className="text-sm text-text-secondary">No upcoming sessions booked.</p>
        ) : (
          <ul className="divide-y divide-border" data-testid="membership-portal-sessions">
            {sessions.map((s) => (
              <li key={s.id} className="flex items-center justify-between py-2 text-sm">
                <span className="text-text-primary">{s.session_date}</span>
                <span className="text-text-secondary">{formatTimeRange(s.start_time, s.end_time)}</span>
                <span className="text-text-muted">{s.hours}h</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
